import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { Battery, Zap, Activity, Calendar } from "lucide-react";
import {
  IQWatchDeviceWithData,
  IQWatchDeviceHistoryResponse,
  IQWatchDeviceHistoryTimeSeries,
} from "../../../types";

interface BatterySolarTabProps {
  deviceWithData: IQWatchDeviceWithData;
  historyData: IQWatchDeviceHistoryResponse | null;
  timeRange: "24h" | "7d" | "1m";
  error: string | null;
}

const BatterySolarTab: React.FC<BatterySolarTabProps> = ({
  deviceWithData,
  historyData,
  timeRange,
  error,
}) => {
  const device = deviceWithData.device;
  const latestData = deviceWithData.latest_data;

  // 格式化时间序列数据为图表数据
  const formatChartData = (timeSeries: IQWatchDeviceHistoryTimeSeries[]) => {
    return timeSeries.map((point) => {
      const date = new Date(point.binned_timestamp);
      let timeLabel = "";

      // Format time label based on range
      if (timeRange === "24h") {
        // 只显示整点时间，格式为 "00:00", "01:00", "02:00" 等
        timeLabel = date.getHours().toString().padStart(2, "0") + ":00";
      } else if (timeRange === "7d") {
        // 只显示天数，格式为 "1", "2", "3" 等
        timeLabel = date.getDate().toString();
      } else if (timeRange === "1m") {
        timeLabel = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }

      return {
        time: timeLabel,
        timestamp: point.binned_timestamp,
        soc: parseFloat(point.soc),
        solar_power: point.solar_power ? parseFloat(point.solar_power) : null,
        min_soc: parseFloat(point.min_soc),
        // 添加小时信息，用于过滤整点数据
        hour: date.getHours(),
        minute: date.getMinutes(),
      };
    });
  };

  const formatValue = (
    value: string | number | undefined | null,
    unit: string = ""
  ) => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? "N/A" : `${num.toFixed(1)}${unit}`;
  };

  const formatDateTime = (dateTime: string | null | undefined) => {
    if (!dateTime) return "N/A";
    try {
      return new Date(dateTime).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  const getSOCColor = (soc: number) => {
    if (soc >= 80) return "text-green-600";
    if (soc >= 50) return "text-yellow-600";
    if (soc >= 20) return "text-orange-600";
    return "text-red-600";
  };

  const getTimeRangeLabel = (range: "24h" | "7d" | "1m") => {
    switch (range) {
      case "24h":
        return "Last 24 Hours";
      case "7d":
        return "Last 7 Days";
      case "1m":
        return "Last 1 Month";
      default:
        return range;
    }
  };

  // 自定义 Tooltip 内容
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-800">
            {timeRange === "24h" ? `Time: ${label}` : `Date: ${label}`}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}:{" "}
              {entry.value !== null ? entry.value.toFixed(1) : "N/A"}
              {entry.dataKey === "soc" || entry.dataKey === "min_soc"
                ? "%"
                : "W"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const chartData = historyData
    ? formatChartData(historyData.data.timeSeries)
    : [];

  return (
    <>
      {/* Current Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Battery SOC</p>
              <p
                className={`text-2xl font-bold ${getSOCColor(
                  parseFloat(
                    latestData?.soc?.toString() || device.last_soc || "0"
                  )
                )}`}
              >
                {formatValue(latestData?.soc || device.last_soc, "%")}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Battery className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Voltage:{" "}
            {formatValue(
              latestData?.battery_voltage || device.last_battery_voltage,
              "V"
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Solar Power</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatValue(
                  latestData?.solar_power || device.last_solar_power,
                  "W"
                )}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Zap className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Load Power</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatValue(
                  latestData?.load_power || device.last_load_power,
                  "W"
                )}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Activity className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Auto Sync</p>
              <p
                className={`text-2xl font-bold ${
                  device.auto_sync_enabled ? "text-green-600" : "text-gray-600"
                }`}
              >
                {device.auto_sync_enabled ? "ON" : "OFF"}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Last sync: {formatDateTime(device.last_sync_at)}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Historical Data Summary */}
      {historyData && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {getTimeRangeLabel(timeRange)} Summary (
            {historyData.data.summary.range})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Deep Discharge Events:</span>
              <span className="font-medium">
                {historyData.data.summary.deepDischargeEvents}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Lowest SOC:</span>
              <span className="font-medium">
                {historyData.data.summary.lowestSoc ? (
                  <>
                    {Number(historyData.data.summary.lowestSoc.value).toFixed(
                      2
                    )}
                    %
                    <span className="text-sm text-gray-500 ml-2">
                      (
                      {new Date(
                        historyData.data.summary.lowestSoc.timestamp
                      ).toLocaleString()}
                      )
                    </span>
                  </>
                ) : (
                  "N/A"
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Charts or Empty State */}
      {chartData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Battery SOC Trend ({getTimeRangeLabel(timeRange)})
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 12 }}
                  interval={
                    timeRange === "24h"
                      ? "preserveStartEnd"
                      : timeRange === "7d"
                      ? "preserveStart"
                      : 2
                  }
                  tickFormatter={(value, index) => {
                    if (timeRange === "24h") {
                      // 只显示整点时间
                      const data = chartData[index];
                      if (data) {
                        const date = new Date(data.timestamp);
                        const minutes = date.getMinutes();
                        // 只显示整点 (分钟为0) 或者第一个和最后一个点
                        if (
                          minutes === 0 ||
                          index === 0 ||
                          index === chartData.length - 1
                        ) {
                          return (
                            date.getHours().toString().padStart(2, "0") + ":00"
                          );
                        }
                        return "";
                      }
                    }
                    return value;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="soc"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: "#10b981", strokeWidth: 2, r: 3 }}
                  name="Battery SOC"
                />
                <Line
                  type="monotone"
                  dataKey="min_soc"
                  stroke="#f59e0b"
                  strokeWidth={1}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Min SOC"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Solar Power Generation ({getTimeRangeLabel(timeRange)})
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 12 }}
                  interval={
                    timeRange === "24h"
                      ? "preserveStartEnd"
                      : timeRange === "7d"
                      ? Math.floor(chartData.length / 7) // 大约显示7个标签
                      : 2
                  }
                  tickFormatter={(value, index) => {
                    if (timeRange === "24h") {
                      // 只显示整点时间
                      const data = chartData[index];
                      if (data) {
                        const date = new Date(data.timestamp);
                        const minutes = date.getMinutes();
                        // 只显示整点 (分钟为0) 或者第一个和最后一个点
                        if (
                          minutes === 0 ||
                          index === 0 ||
                          index === chartData.length - 1
                        ) {
                          return (
                            date.getHours().toString().padStart(2, "0") + ":00"
                          );
                        }
                        return "";
                      }
                    }
                    return value;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="solar_power" fill="#10b981" name="Solar Power" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        historyData && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Historical Data Available
              </h3>
              <p className="text-gray-500 mb-4">
                No historical data found for the selected time range (
                {getTimeRangeLabel(timeRange)}).
              </p>
              <p className="text-sm text-gray-400">
                Historical data may not be available yet or the device may not
                have been reporting during this period.
              </p>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default BatterySolarTab;
